import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Users, BookOpen, BarChart3, Activity, Award, Trash2, Edit, Plus, Search, CreditCard, UserPlus, Network, Server, Timer, ShieldAlert } from "lucide-react";

// Live HH:MM:SS countdown to an ISO timestamp. Returns "00:00:00" once elapsed.
function fmtCountdown(expiresAt, nowMs) {
  if (!expiresAt) return "—";
  const ms = new Date(expiresAt).getTime() - nowMs;
  if (ms <= 0) return "00:00:00";
  const total = Math.floor(ms / 1000);
  const h = String(Math.floor(total / 3600)).padStart(2, "0");
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function StatCard({ label, value, hint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="font-display text-3xl font-bold text-slate-900 mt-2">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

// "2h ago" / "3d ago" from an ISO timestamp.
function fmtRelative(iso) {
  if (!iso) return "Never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function BarList({ title, rows, labelKey, max, testid }) {
  const top = Math.max(max || 0, 1);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={testid}>
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">{title}</div>
      <div className="space-y-1.5">
        {(!rows || rows.length === 0) && <div className="text-sm text-slate-500">No data yet.</div>}
        {(rows || []).map((row, i) => (
          <div key={i} className="flex items-center gap-3 text-sm">
            <div className="text-slate-600 w-20 sm:w-32 truncate" title={row[labelKey]}>{row[labelKey] || "-"}</div>
            <div className="flex-1 h-2 bg-slate-100 rounded">
              <div className="h-2 bg-blue-600 rounded" style={{ width: `${Math.min(100, Math.round((row.count / top) * 100))}%` }} />
            </div>
            <div className="font-mono w-10 text-right">{row.count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-slate-500">Loading…</div>;
  const maxRegion = Math.max(0, ...(d.visits_by_region || []).map((r) => r.count));
  const maxCountry = Math.max(0, ...(d.top_countries || []).map((r) => r.count));
  const maxVisitDay = Math.max(0, ...(d.visits_by_day || []).map((r) => r.count));
  const maxSignupDay = Math.max(0, ...(d.signups_by_day || []).map((r) => r.count));
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total users" value={d.total_users} />
        <StatCard label="Active (7d)" value={d.active_users_7d ?? 0} hint="logged in last 7 days" />
        <StatCard label="Active (30d)" value={d.active_users_30d ?? 0} hint="logged in last 30 days" />
        <StatCard label="Active subs" value={d.active_subscriptions} />
        <StatCard label="Visits (30d)" value={d.total_visits ?? 0} hint="unique sessions" />
        <StatCard label="Lab completions" value={d.lab_completions} />
        <StatCard label="Certificates" value={d.certificates_issued} />
        <StatCard label="Running instances" value={d.running_instances} />
        <StatCard label="Published labs" value={`${d.published_labs}/${d.total_labs}`} />
        <StatCard label="Revenue (paid)" value={`$${d.total_revenue}`} />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <BarList title="Visits by region (30d)" rows={d.visits_by_region} labelKey="region" max={maxRegion} testid="analytics-visits-by-region" />
        <BarList title="Top countries (30d)" rows={d.top_countries} labelKey="country" max={maxCountry} testid="analytics-top-countries" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4" data-testid="analytics-subscription-funnel">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">Subscription funnel (all-time)</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4" data-testid="funnel-page-views">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Landed on page</div>
            <div className="font-display text-3xl font-bold text-slate-900 mt-2">{d.subscription_funnel?.page_views ?? 0}</div>
            <div className="text-xs text-slate-500 mt-1">opened /subscription</div>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4" data-testid="funnel-subscribe-clicks">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Clicked subscribe</div>
            <div className="font-display text-3xl font-bold text-slate-900 mt-2">{d.subscription_funnel?.subscribe_clicks ?? 0}</div>
            <div className="text-xs text-slate-500 mt-1">pressed the subscribe button</div>
          </div>
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-4" data-testid="funnel-conversion">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-blue-700">Click-through rate</div>
            <div className="font-display text-3xl font-bold text-blue-700 mt-2">{d.subscription_funnel?.conversion_rate ?? 0}%</div>
            <div className="text-xs text-blue-700/70 mt-1">clicks ÷ page views</div>
          </div>
        </div>
        <div className="mt-4 h-2 bg-slate-100 rounded overflow-hidden">
          <div className="h-2 bg-blue-600 rounded" style={{ width: `${Math.min(100, d.subscription_funnel?.conversion_rate ?? 0)}%` }} />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="analytics-visits-by-day">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Visits (by day)</div>
          <div className="space-y-1.5">
            {(d.visits_by_day || []).length === 0 && <div className="text-sm text-slate-500">No data yet.</div>}
            {(d.visits_by_day || []).map((row) => (
              <div key={row.date} className="flex items-center gap-3 text-sm">
                <div className="font-mono text-slate-500 w-24">{row.date || "-"}</div>
                <div className="flex-1 h-2 bg-slate-100 rounded">
                  <div className="h-2 bg-indigo-600 rounded" style={{ width: `${Math.min(100, Math.round((row.count / Math.max(maxVisitDay, 1)) * 100))}%` }} />
                </div>
                <div className="font-mono w-8 text-right">{row.count}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Recent signups (by day)</div>
          <div className="space-y-1.5">
            {d.signups_by_day.length === 0 && <div className="text-sm text-slate-500">No data yet.</div>}
            {d.signups_by_day.map((row) => (
              <div key={row.date} className="flex items-center gap-3 text-sm">
                <div className="font-mono text-slate-500 w-24">{row.date || "-"}</div>
                <div className="flex-1 h-2 bg-slate-100 rounded">
                  <div className="h-2 bg-blue-600 rounded" style={{ width: `${Math.min(100, Math.round((row.count / Math.max(maxSignupDay, 1)) * 100))}%` }} />
                </div>
                <div className="font-mono w-8 text-right">{row.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const EMPTY_NEW_USER = { email: "", name: "", password: "", role: "user", grant_subscription: false, plan: "monthly", duration_days: 30 };

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [activityUser, setActivityUser] = useState(null);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [nu, setNu] = useState(EMPTY_NEW_USER);
  const [nuErr, setNuErr] = useState("");
  const [subUser, setSubUser] = useState(null);
  const [sub, setSub] = useState({ status: "active", plan: "monthly", duration_days: 30 });
  const [subErr, setSubErr] = useState("");

  const load = async () => {
    const { data } = await api.get(`/admin/users${q ? `?search=${encodeURIComponent(q)}` : ""}`);
    setUsers(data.users);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const del = async (id) => {
    if (!confirm("Delete this user?")) return;
    await api.delete(`/admin/users/${id}`);
    toast.success("User deleted");
    load();
  };

  const createUser = async () => {
    setNuErr("");
    try {
      await api.post("/admin/users", {
        ...nu,
        duration_days: nu.grant_subscription ? (parseInt(nu.duration_days) || undefined) : undefined,
      });
      toast.success("User created");
      setCreating(false); setNu(EMPTY_NEW_USER); load();
    } catch (e) { setNuErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const openSub = (u) => {
    setSubUser(u);
    setSub({ status: "active", plan: u.subscription_plan || "monthly", duration_days: 30 });
    setSubErr("");
  };
  const saveSub = async () => {
    setSubErr("");
    try {
      await api.post(`/admin/users/${subUser.id}/subscription`, {
        status: sub.status,
        plan: sub.status === "active" ? sub.plan : null,
        duration_days: sub.status === "active" ? (parseInt(sub.duration_days) || undefined) : undefined,
      });
      toast.success(sub.status === "active" ? "Subscription granted" : "Subscription revoked");
      setSubUser(null); load();
    } catch (e) { setSubErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email or name…" className="pl-9"
            data-testid="admin-users-search" />
        </div>
        <Button onClick={load} variant="outline" data-testid="admin-users-search-button">Search</Button>
        <Button onClick={() => { setNu(EMPTY_NEW_USER); setNuErr(""); setCreating(true); }}
          className="sm:ml-auto" data-testid="admin-user-create-button">
          <UserPlus className="w-4 h-4 mr-1" /> New user
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Email</th>
              <th className="text-left px-4 py-3 font-semibold">Name</th>
              <th className="text-left px-4 py-3 font-semibold">Role</th>
              <th className="text-left px-4 py-3 font-semibold">Subscription</th>
              <th className="text-left px-4 py-3 font-semibold">2FA</th>
              <th className="text-left px-4 py-3 font-semibold">Last login</th>
              <th className="text-left px-4 py-3 font-semibold">Last login IP</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100" data-testid={`admin-user-row-${u.email}`}>
                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{u.role}</Badge></td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className={u.subscription_status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50"}>
                    {u.subscription_status}{u.subscription_plan ? ` · ${u.subscription_plan}` : ""}
                  </Badge>
                </td>
                <td className="px-4 py-3">{u.two_factor_enabled ? "✓" : "-"}</td>
                <td className="px-4 py-3 text-xs text-slate-600" data-testid={`admin-user-lastlogin-${u.email}`} title={u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "Never"}>{fmtRelative(u.last_login_at)}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-600" data-testid={`admin-user-lastip-${u.email}`}>{u.last_login_ip || "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setActivityUser(u)} title="View activity, login IPs and lab history" data-testid={`admin-user-activity-${u.email}`}>
                    <Activity className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openSub(u)} title="Manage subscription" data-testid={`admin-user-subscription-${u.email}`}>
                    <CreditCard className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => del(u.id)} data-testid={`admin-user-delete-${u.email}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create user dialog */}
      <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create user</DialogTitle>
            <DialogDescription>Admin-create an account. The user can log in immediately with this password (2FA starts off).</DialogDescription>
          </DialogHeader>
          {nuErr && <Alert variant="destructive"><AlertDescription>{nuErr}</AlertDescription></Alert>}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Email</Label>
              <Input type="email" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} data-testid="admin-newuser-email" /></div>
            <div><Label>Name</Label>
              <Input value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} data-testid="admin-newuser-name" /></div>
            <div><Label>Password</Label>
              <Input type="text" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })}
                placeholder="min 8 chars" className="font-mono" data-testid="admin-newuser-password" /></div>
            <div>
              <Label>Role</Label>
              <Select value={nu.role} onValueChange={(v) => setNu({ ...nu, role: v })}>
                <SelectTrigger data-testid="admin-newuser-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <Switch checked={nu.grant_subscription} onCheckedChange={(v) => setNu({ ...nu, grant_subscription: v })}
                data-testid="admin-newuser-grant-switch" />
              <Label>Grant subscription on creation</Label>
            </div>
            {nu.grant_subscription && (
              <>
                <div>
                  <Label>Plan</Label>
                  <Select value={nu.plan} onValueChange={(v) => setNu({ ...nu, plan: v })}>
                    <SelectTrigger data-testid="admin-newuser-plan"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly (30 days)</SelectItem>
                      <SelectItem value="yearly">Yearly (365 days)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Duration (days, optional)</Label>
                  <Input type="number" value={nu.duration_days}
                    onChange={(e) => setNu({ ...nu, duration_days: e.target.value })}
                    placeholder="defaults to plan length" data-testid="admin-newuser-duration" /></div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={createUser} data-testid="admin-newuser-save">Create user</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage subscription dialog */}
      <Dialog open={!!subUser} onOpenChange={(o) => !o && setSubUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage subscription</DialogTitle>
            <DialogDescription>{subUser?.email} · current: {subUser?.subscription_status}{subUser?.subscription_plan ? ` · ${subUser.subscription_plan}` : ""}</DialogDescription>
          </DialogHeader>
          {subErr && <Alert variant="destructive"><AlertDescription>{subErr}</AlertDescription></Alert>}
          <div className="grid gap-4">
            <div>
              <Label>Status</Label>
              <Select value={sub.status} onValueChange={(v) => setSub({ ...sub, status: v })}>
                <SelectTrigger data-testid="admin-sub-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active (grant)</SelectItem>
                  <SelectItem value="inactive">Inactive (revoke)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {sub.status === "active" && (
              <>
                <div>
                  <Label>Plan</Label>
                  <Select value={sub.plan} onValueChange={(v) => setSub({ ...sub, plan: v })}>
                    <SelectTrigger data-testid="admin-sub-plan"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly (30 days)</SelectItem>
                      <SelectItem value="yearly">Yearly (365 days)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Duration (days, optional)</Label>
                  <Input type="number" value={sub.duration_days}
                    onChange={(e) => setSub({ ...sub, duration_days: e.target.value })}
                    placeholder="defaults to plan length" data-testid="admin-sub-duration" /></div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubUser(null)}>Cancel</Button>
            <Button onClick={saveSub} data-testid="admin-sub-save">{sub.status === "active" ? "Grant subscription" : "Revoke subscription"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UserActivityDialog user={activityUser} onClose={() => setActivityUser(null)} />
    </div>
  );
}

function UserActivityDialog({ user, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user) { setData(null); setErr(""); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/users/${user.id}/activity`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setErr(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
    return () => { alive = false; };
  }, [user]);

  const c = data?.counts;
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="admin-user-activity-dialog">
        <DialogHeader>
          <DialogTitle>Account activity</DialogTitle>
          <DialogDescription>{user?.email} · last login IP {user?.last_login_ip || "unknown"}</DialogDescription>
        </DialogHeader>
        {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
        {!data && !err && <div className="text-sm text-slate-500 py-6">Loading…</div>}
        {data && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard label="Labs launched" value={c.labs_launched} />
              <StatCard label="Running now" value={c.labs_running} />
              <StatCard label="Completed" value={c.labs_completed} />
              <StatCard label="Failed logins" value={c.failed_logins} />
            </div>

            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Login history (last 20)</div>
              {data.logins.length === 0 ? (
                <p className="text-sm text-slate-500">No logins recorded since audit logging was enabled.</p>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-x-auto">
                  <table className="w-full min-w-[560px] text-xs" data-testid="admin-activity-logins">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">When</th>
                        <th className="text-left px-3 py-2 font-semibold">IP</th>
                        <th className="text-left px-3 py-2 font-semibold">Result</th>
                        <th className="text-left px-3 py-2 font-semibold">Device</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.logins.map((l, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="px-3 py-2 whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleString() : "—"}</td>
                          <td className="px-3 py-2 font-mono">{l.ip || "—"}</td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className={l.success ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}>
                              {l.success ? "success" : l.reason || "failed"}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-slate-500 truncate max-w-[220px]" title={l.user_agent}>{l.user_agent || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Lab instances (last 25)</div>
              {data.instances.length === 0 ? (
                <p className="text-sm text-slate-500">This account has never launched a lab.</p>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-x-auto">
                  <table className="w-full min-w-[620px] text-xs" data-testid="admin-activity-instances">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Lab</th>
                        <th className="text-left px-3 py-2 font-semibold">Status</th>
                        <th className="text-left px-3 py-2 font-semibold">Started</th>
                        <th className="text-left px-3 py-2 font-semibold">Tunnel IP</th>
                        <th className="text-left px-3 py-2 font-semibold">VPN certificate (CN)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.instances.map((i, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="px-3 py-2 font-mono">{i.lab_slug || "—"}</td>
                          <td className="px-3 py-2">{i.status}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{i.started_at ? new Date(i.started_at).toLocaleString() : "—"}</td>
                          <td className="px-3 py-2 font-mono">{i.peer_ip || "—"}</td>
                          <td className="px-3 py-2 font-mono text-slate-500">{i.range_instance_id || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function fmtBytes(n) {
  const b = Number(n || 0);
  if (b < 1024) return `${b} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = b / 1024, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(1)} ${units[i]}`;
}

function SecurityTab() {
  const [vpn, setVpn] = useState(null);
  const [events, setEvents] = useState([]);
  const [failedOnly, setFailedOnly] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    try {
      const [v, e] = await Promise.all([
        api.get("/admin/security/vpn-sessions"),
        api.get(`/admin/security/login-events?limit=100${failedOnly ? "&only_failed=true" : ""}`),
      ]);
      setVpn(v.data);
      setEvents(e.data.events);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [failedOnly]);

  return (
    <div className="space-y-8" data-testid="admin-security-tab">
      {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Live VPN sessions</h2>
          <p className="text-sm text-slate-500">Who is connected to the Advanced range right now, and where from.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="admin-security-refresh">Refresh</Button>
      </div>

      {vpn?.mode === "mock" && (
        <Alert data-testid="admin-security-mock-notice">
          <AlertDescription>
            The advanced provisioner is in <strong>mock</strong> mode, so there is no real VPN server to query.
            Set <code>ADVANCED_PROVISIONER_MODE=range</code> and the Range Agent env vars in production.
          </AlertDescription>
        </Alert>
      )}
      {vpn?.mode === "range" && !vpn?.status_file_present && (
        <Alert variant="destructive" data-testid="admin-security-nostatus-notice">
          <AlertDescription>
            The OpenVPN server is not writing a status file, so connected clients cannot be listed.
            Add <code>status /etc/openvpn/openvpn-status.log</code> and <code>status-version 2</code> to
            the server config and restart <code>openvpn@server</code>.
          </AlertDescription>
        </Alert>
      )}
      {vpn?.error && vpn?.mode === "range" && (
        <Alert variant="destructive"><AlertDescription>Range Agent error: {vpn.error}</AlertDescription></Alert>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[980px] text-sm" data-testid="admin-vpn-sessions-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">User</th>
              <th className="text-left px-4 py-3 font-semibold">Public IP</th>
              <th className="text-left px-4 py-3 font-semibold">Tunnel IP</th>
              <th className="text-left px-4 py-3 font-semibold">Lab</th>
              <th className="text-left px-4 py-3 font-semibold">Connected since</th>
              <th className="text-right px-4 py-3 font-semibold">In / Out</th>
              <th className="text-right px-4 py-3 font-semibold">Blocked egress</th>
            </tr>
          </thead>
          <tbody>
            {(vpn?.sessions || []).length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-testid="admin-vpn-empty">No active VPN sessions.</td></tr>
            ) : vpn.sessions.map((s, i) => (
              <tr key={i} className="border-b border-slate-100" data-testid={`admin-vpn-session-${i}`}>
                <td className="px-4 py-3 font-mono text-xs">{s.user_email}</td>
                <td className="px-4 py-3 font-mono text-xs font-semibold">{s.public_ip || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{s.tunnel_ip || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{s.lab_slug || "—"}</td>
                <td className="px-4 py-3 text-xs whitespace-nowrap">{s.connected_since || "—"}</td>
                <td className="px-4 py-3 text-xs text-right whitespace-nowrap">{fmtBytes(s.bytes_received)} / {fmtBytes(s.bytes_sent)}</td>
                <td className="px-4 py-3 text-right">
                  {s.blocked_packets > 0 ? (
                    <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200" data-testid={`admin-vpn-blocked-${i}`}>
                      {s.blocked_packets} pkts
                    </Badge>
                  ) : <span className="text-xs text-slate-400">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(vpn?.egress_without_session || []).length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-1">Blocked egress with no live tunnel</h2>
          <p className="text-sm text-slate-500 mb-3">Ranges that tried to reach the outside world and were refused, but are not connected right now.</p>
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm" data-testid="admin-egress-orphan-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">User</th>
                  <th className="text-left px-4 py-3 font-semibold">Lab</th>
                  <th className="text-left px-4 py-3 font-semibold">VPN certificate (CN)</th>
                  <th className="text-right px-4 py-3 font-semibold">Blocked</th>
                </tr>
              </thead>
              <tbody>
                {vpn.egress_without_session.map((o, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-4 py-3 font-mono text-xs">{o.user_email}</td>
                    <td className="px-4 py-3 font-mono text-xs">{o.lab_slug || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{o.instance_id}</td>
                    <td className="px-4 py-3 text-right text-xs">{o.blocked_packets} pkts · {fmtBytes(o.blocked_bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Login audit trail</h2>
            <p className="text-sm text-slate-500">Every sign-in attempt with its source IP. Repeated failures from one IP mean credential stuffing.</p>
          </div>
          <Button variant={failedOnly ? "default" : "outline"} onClick={() => setFailedOnly(!failedOnly)} data-testid="admin-security-failed-toggle">
            {failedOnly ? "Showing failures only" : "Show failures only"}
          </Button>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm" data-testid="admin-login-events-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">When</th>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-left px-4 py-3 font-semibold">IP</th>
                <th className="text-left px-4 py-3 font-semibold">Result</th>
                <th className="text-left px-4 py-3 font-semibold">Device</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500" data-testid="admin-login-events-empty">No login events recorded yet.</td></tr>
              ) : events.map((e, i) => (
                <tr key={i} className="border-b border-slate-100" data-testid={`admin-login-event-${i}`}>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">{e.created_at ? new Date(e.created_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{e.email}</td>
                  <td className="px-4 py-3 font-mono text-xs font-semibold">{e.ip || "—"}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={e.success ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}>
                      {e.success ? "success" : e.reason || "failed"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 truncate max-w-[260px]" title={e.user_agent}>{e.user_agent || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const EMPTY_LAB = {
  slug: "", title: "", level: "beginner", sort_order: 99,
  description: "", objective: "", guide_markdown: "", solution_markdown: "", flag: "ILFAL{}",
  duration_minutes: 60, is_free: false, is_published: true,
};

function LabsTab() {
  const [labs, setLabs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_LAB);
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/labs");
    setLabs(data.labs);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing("new"); setForm(EMPTY_LAB); setErr(""); };
  const openEdit = (lab) => { setEditing(lab.id); setForm({ ...lab }); setErr(""); };

  const save = async () => {
    setErr("");
    try {
      if (editing === "new") {
        await api.post("/admin/labs", form);
        toast.success("Lab created");
      } else {
        await api.put(`/admin/labs/${editing}`, form);
        toast.success("Lab updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this lab?")) return;
    await api.delete(`/admin/labs/${id}`);
    toast.success("Lab deleted");
    load();
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div className="text-sm text-slate-600">{labs.length} labs · dynamic CRUD, no redeployment needed</div>
        <Button onClick={openCreate} data-testid="admin-lab-create-button">
          <Plus className="w-4 h-4 mr-1" /> New lab
        </Button>
      </div>

      <div className="space-y-2">
        {labs.map((lab) => (
          <div key={lab.id} data-testid={`admin-lab-row-${lab.slug}`}
            className="bg-white border border-slate-200 rounded-lg p-3 sm:p-4 flex items-center gap-2 sm:gap-4">
            <div className="font-mono text-xs text-slate-400 w-8 sm:w-12 text-center shrink-0">{lab.sort_order}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-900">{lab.title}</span>
                <Badge variant="outline" className="capitalize">{lab.level}</Badge>
                {lab.is_free && <Badge className="bg-blue-600">Free</Badge>}
                {!lab.is_published && <Badge variant="outline" className="bg-slate-100">Unpublished</Badge>}
              </div>
              <div className="text-xs text-slate-500 font-mono mt-1 truncate">{lab.slug}</div>
            </div>
            <div className="flex shrink-0">
            <Button size="sm" variant="ghost" onClick={() => openEdit(lab)} data-testid={`admin-lab-edit-${lab.slug}`}><Edit className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" onClick={() => del(lab.id)} data-testid={`admin-lab-delete-${lab.slug}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Create lab" : "Edit lab"}</DialogTitle>
          </DialogHeader>
          {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Slug</Label><Input value={form.slug} disabled={editing !== "new"}
              onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="admin-lab-slug-input" /></div>
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-lab-title-input" /></div>
            <div>
              <Label>Level</Label>
              <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                <SelectTrigger data-testid="admin-lab-level-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">AI Security Analyst</SelectItem>
                  <SelectItem value="intermediate">AI Red Teamer</SelectItem>
                  <SelectItem value="advanced">AI Red Team Operator</SelectItem>
                  <SelectItem value="developer">AI Application Security Engineer</SelectItem>
                  <SelectItem value="supplychain">AI Supply Chain Security</SelectItem>
                  <SelectItem value="infra">AI Infrastructure Security</SelectItem>
                  <SelectItem value="claudecode101">Claude Code 101</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Sort order</Label><Input type="number" value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} /></div>
            <div><Label>Duration (min)</Label><Input type="number" value={form.duration_minutes}
              onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })} /></div>
            <div><Label>Flag</Label><Input value={form.flag} className="font-mono"
              onChange={(e) => setForm({ ...form, flag: e.target.value })} data-testid="admin-lab-flag-input" /></div>
            <div className="sm:col-span-2"><Label>Description (short)</Label>
              <Textarea rows={2} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Objective</Label>
              <Textarea rows={2} value={form.objective}
                onChange={(e) => setForm({ ...form, objective: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Guide (markdown)</Label>
              <Textarea rows={10} className="font-mono text-sm" value={form.guide_markdown}
                onChange={(e) => setForm({ ...form, guide_markdown: e.target.value })}
                data-testid="admin-lab-guide-input" /></div>
            <div className="sm:col-span-2"><Label>Solution (markdown, hidden behind &quot;Reveal solution&quot;)</Label>
              <Textarea rows={8} className="font-mono text-sm" value={form.solution_markdown || ""}
                onChange={(e) => setForm({ ...form, solution_markdown: e.target.value })}
                data-testid="admin-lab-solution-input" /></div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_free} onCheckedChange={(v) => setForm({ ...form, is_free: v })}
                data-testid="admin-lab-free-switch" />
              <Label>Free lab</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })}
                data-testid="admin-lab-published-switch" />
              <Label>Published</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} data-testid="admin-lab-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RangesTab() {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const load = () =>
    api.get("/admin/instances").then((r) =>
      setItems((r.data.instances || []).filter((i) => i.lab_kind === "network")));
  useEffect(() => {
    load();
    const refresh = setInterval(load, 10000);
    const tick = setInterval(() => setNowMs(Date.now()), 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, []);
  const teardown = async (id) => {
    if (!confirm("Force-teardown this VPN range? The user's tunnel + targets are destroyed immediately and their .ovpn stops working.")) return;
    setBusyId(id);
    try {
      await api.post(`/admin/instances/${id}/terminate`);
      toast.success("Range torn down");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };
  return (
    <div data-testid="admin-ranges-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Network className="w-4 h-4 text-indigo-600" /> Active VPN ranges
        </div>
        <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200" data-testid="admin-ranges-count">
          {items.length} live
        </Badge>
        <span className="text-xs text-slate-500">auto-refreshes every 10s</span>
      </div>
      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg px-4 py-12 text-center text-slate-500" data-testid="admin-ranges-empty">
          No active VPN ranges right now.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {items.map((i) => {
            const cd = fmtCountdown(i.expires_at, nowMs);
            const expired = cd === "00:00:00";
            return (
              <div key={i.id} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`admin-range-card-${i.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="font-mono text-sm font-semibold text-slate-900 truncate">{i.user_email}</div>
                    <div className="text-xs text-slate-500 truncate">{i.lab_slug}</div>
                  </div>
                  <div className={`flex items-center gap-1 font-mono text-sm font-bold ${expired ? "text-rose-600" : "text-slate-900"}`} data-testid={`admin-range-countdown-${i.id}`}>
                    <Timer className="w-3.5 h-3.5" /> {cd}
                  </div>
                </div>
                <div className="text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-1 flex items-center gap-1">
                  <Server className="w-3 h-3" /> Range {i.container_id}
                </div>
                {(i.target_ips || []).length === 0 ? (
                  <div className="text-xs text-slate-500 mb-3">No targets reported.</div>
                ) : (
                  <div className="space-y-1 mb-3" data-testid={`admin-range-targets-${i.id}`}>
                    {(i.target_ips || []).map((t) => (
                      <div key={t.name} className="font-mono text-xs text-slate-700">
                        {t.ip} <span className="text-slate-400">· {t.name}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="text-[11px] text-slate-400 mb-3">
                  Started {new Date(i.started_at).toLocaleString()}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full text-rose-700 hover:text-rose-700 border-rose-200"
                  disabled={busyId === i.id}
                  onClick={() => teardown(i.id)}
                  data-testid={`admin-range-teardown-${i.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> {busyId === i.id ? "Tearing down…" : "Force-teardown"}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InstancesTab() {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const load = () => api.get("/admin/instances").then((r) => setItems(r.data.instances));
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);
  const terminate = async (id) => {
    if (!confirm("Force-terminate this user's lab? They will lose any unsaved progress.")) return;
    setBusyId(id);
    try {
      await api.post(`/admin/instances/${id}/terminate`);
      toast.success("Lab terminated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusyId(null); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left px-4 py-3 font-semibold">User</th>
            <th className="text-left px-4 py-3 font-semibold">Lab</th>
            <th className="text-left px-4 py-3 font-semibold">Container</th>
            <th className="text-left px-4 py-3 font-semibold">Started</th>
            <th className="text-left px-4 py-3 font-semibold">Expires</th>
            <th className="text-right px-4 py-3 font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.id} className="border-b border-slate-100" data-testid={`admin-instance-row-${i.id}`}>
              <td className="px-4 py-3 font-mono text-xs">{i.user_email}</td>
              <td className="px-4 py-3">{i.lab_slug}</td>
              <td className="px-4 py-3 font-mono text-xs">{i.container_id?.slice(0, 12)}</td>
              <td className="px-4 py-3 text-xs">{new Date(i.started_at).toLocaleString()}</td>
              <td className="px-4 py-3 text-xs">{new Date(i.expires_at).toLocaleString()}</td>
              <td className="px-4 py-3 text-right">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-rose-700 hover:text-rose-700"
                  disabled={busyId === i.id}
                  onClick={() => terminate(i.id)}
                  data-testid={`admin-instance-terminate-${i.id}`}
                >
                  Terminate
                </Button>
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No running instances.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function CertificatesTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/certificates").then((r) => setItems(r.data.certificates)); }, []);
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
      <table className="w-full min-w-[560px] text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left px-4 py-3 font-semibold">Verification ID</th>
            <th className="text-left px-4 py-3 font-semibold">Holder</th>
            <th className="text-left px-4 py-3 font-semibold">Level</th>
            <th className="text-left px-4 py-3 font-semibold">Issued</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-b border-slate-100">
              <td className="px-4 py-3 font-mono text-xs">{c.verification_id}</td>
              <td className="px-4 py-3">{c.user_name}</td>
              <td className="px-4 py-3 capitalize">{c.level}</td>
              <td className="px-4 py-3 text-xs">{new Date(c.issued_at).toLocaleDateString()}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">No certificates issued yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function Admin() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      <div className="mb-6 sm:mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">Admin</div>
        <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-slate-900">Control center</h1>
      </div>

      <Tabs defaultValue="analytics">
        <TabsList className="mb-6 flex flex-wrap h-auto justify-start gap-1 w-full sm:w-auto" data-testid="admin-tabs">
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics"><BarChart3 className="w-4 h-4 mr-1" /> Analytics</TabsTrigger>
          <TabsTrigger value="labs" data-testid="admin-tab-labs"><BookOpen className="w-4 h-4 mr-1" /> Labs</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-1" /> Users</TabsTrigger>
          <TabsTrigger value="instances" data-testid="admin-tab-instances"><Activity className="w-4 h-4 mr-1" /> Instances</TabsTrigger>
          <TabsTrigger value="ranges" data-testid="admin-tab-ranges"><Network className="w-4 h-4 mr-1" /> Ranges</TabsTrigger>
          <TabsTrigger value="security" data-testid="admin-tab-security"><ShieldAlert className="w-4 h-4 mr-1" /> Security</TabsTrigger>
          <TabsTrigger value="certificates" data-testid="admin-tab-certificates"><Award className="w-4 h-4 mr-1" /> Certificates</TabsTrigger>
        </TabsList>
        <TabsContent value="analytics"><Analytics /></TabsContent>
        <TabsContent value="labs"><LabsTab /></TabsContent>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="instances"><InstancesTab /></TabsContent>
        <TabsContent value="ranges"><RangesTab /></TabsContent>
        <TabsContent value="security"><SecurityTab /></TabsContent>
        <TabsContent value="certificates"><CertificatesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
